import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Role } from '@ithelpdesk/shared';
import { apiPost } from '../api/client';
import { queryKeys } from '../api/queries';
import { useSession } from '../api/session';
import { Badge } from '../ui/Badge/Badge';
import { cx } from '../ui/cx';
import { useEscapeKey, useOnClickOutside } from '../ui/hooks';
import { ChevronDownIcon, LogoutIcon, SoundOffIcon, SoundOnIcon } from './icons';
import { resetLiveCounts } from '../features/live/liveCounts';
import { useSoundMuted } from '../features/live/notificationSound';
import { NotificationBellNavIcon } from '../features/notifications/NotificationBellNavIcon';
import { identityRoleLabel, roleLabel } from './roles';
import styles from './UserMenu.module.css';
import { AvatarPhoto } from '../ui/Avatar/Avatar';

function formatRole(role: Role): string {
  return roleLabel(role);
}

function initialsFor(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + last).toUpperCase();
}

/**
 * Organizational identity (name + primary role) and account actions. The
 * role line is display-only — authorization is always the server's call.
 */
export function UserMenu() {
  const { data: session } = useSession();
  const [open, setOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [soundMuted, setSoundMuted] = useSoundMuted();

  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useOnClickOutside(wrapperRef, () => setOpen(false), open);
  useEscapeKey(() => {
    setOpen(false);
    triggerRef.current?.focus();
  }, open);

  // Menu keyboard model: focus the first item on open; arrows / Home / End move.
  useEffect(() => {
    if (open) panelRef.current?.querySelector<HTMLElement>('[role^="menuitem"]:not([disabled])')?.focus();
  }, [open]);

  function onMenuKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    const items = Array.from(panelRef.current?.querySelectorAll<HTMLElement>('[role^="menuitem"]:not([disabled])') ?? []);
    if (items.length === 0) return;
    const i = items.indexOf(document.activeElement as HTMLElement);
    let next: HTMLElement | undefined;
    if (e.key === 'ArrowDown') next = items[(i + 1) % items.length];
    else if (e.key === 'ArrowUp') next = items[(i - 1 + items.length) % items.length];
    else if (e.key === 'Home') next = items[0];
    else if (e.key === 'End') next = items[items.length - 1];
    else if (e.key === 'Tab') setOpen(false);
    if (next) {
      e.preventDefault();
      next.focus();
    }
  }

  if (!session) return null;
  const roleLine = identityRoleLabel(session.roles, session.customRoles);

  async function handleLogout(): Promise<void> {
    setLoggingOut(true);
    try {
      await apiPost('/auth/logout');
    } catch {
      // Fall through regardless — the client-side session cache is dropped
      // either way below, so a failed network call never leaves the UI
      // stuck presenting authenticated chrome for a session the person
      // asked to end.
    } finally {
      queryClient.removeQueries({ queryKey: queryKeys.me() });
      resetLiveCounts();
      setOpen(false);
      setLoggingOut(false);
      navigate('/login', { replace: true });
    }
  }

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`Account: ${session.user.displayName}${roleLine ? `, ${roleLine}` : ''}`}
      >
        {/* The signed-in person's own avatar: accent-filled, as in the
            reference (other people's avatars keep ui/Avatar's name hues).
            Decorative — the trigger's aria-label already names them. */}
        <span className={styles.avatar} aria-hidden="true">
          {initialsFor(session.user.displayName)}
          <AvatarPhoto userId={session.user.id} />
        </span>
        <span className={styles.identity}>
          <span className={styles.name}>{session.user.displayName}</span>
          {roleLine && <span className={styles.role}>{roleLine}</span>}
        </span>
        <ChevronDownIcon className={styles.chevron} />
      </button>
      {open && (
        <div ref={panelRef} className={styles.panel} role="menu" aria-label="Account" onKeyDown={onMenuKeyDown}>
          <div className={styles.header}>
            <div className={styles.displayName}>{session.user.displayName}</div>
            <div className={styles.email}>{session.user.email}</div>
            <div className={styles.roles}>
              {session.roles.map((role) => (
                <Badge key={role} tone="neutral">
                  {formatRole(role)}
                </Badge>
              ))}
              {(session.customRoles ?? []).map((role) => (
                <Badge key={role.id} tone="neutral">
                  {role.name}
                </Badge>
              ))}
            </div>
          </div>
          <div className={styles.divider} />
          <button
            type="button"
            role="menuitem"
            className={styles.menuButton}
            onClick={() => {
              setOpen(false);
              navigate('/notifications');
            }}
          >
            <NotificationBellNavIcon />
            Notifications &amp; preferences
          </button>
          {/* Per-browser preference (localStorage). Announced as a switch-like menu item. */}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={!soundMuted}
            className={styles.menuButton}
            onClick={() => setSoundMuted(!soundMuted)}
          >
            {soundMuted ? <SoundOffIcon /> : <SoundOnIcon />}
            New-ticket sound: {soundMuted ? 'Off' : 'On'}
          </button>
          <button
            type="button"
            role="menuitem"
            className={cx(styles.menuButton, styles.danger)}
            onClick={handleLogout}
            disabled={loggingOut}
          >
            <LogoutIcon />
            {loggingOut ? 'Signing out…' : 'Log out'}
          </button>
        </div>
      )}
    </div>
  );
}
